import { useRef, useState } from 'react';
import { BsArrowRightCircle } from 'react-icons/bs';
import styled from 'styled-components';

import { EmojiMartData } from '@emoji-mart/data';

import { useAppContext } from '../../appContext';
import ArrowRightIcon from '../../assets/arrow-right.svg';
import useUserSession from '../../hooks/useUserSession';
import { openUserForm } from '../../store/actions';
import { useStateContext } from '../../store/stateContext';
import { User } from '../../types';
import EmojiInput from '../emoji-input';

// Styled components
const Container = styled.div`
  position: relative !important;
  width: 100% !important;
  /*margin-bottom: 8px !important;*/ /* 0.5rem = 8px */
  z-index: 1 !important;
`;

const InputContainer = styled.div<{ width?: string }>`
  position: relative;
  font-family: Sora !important;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: ${props => props.width || '100%'};
`;

const TextareaWrapper = styled.div`
  position: relative;
  font-family: Sora !important;
  display: flex;
  align-items: center;
  width: 100%;
`;

const StyledTextarea = styled.textarea<{ isDarkMode: boolean; isPurchase: boolean; overrideInputbg?: string }>`
  && {
    all: revert !important;
    font-family: Sora !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    flex-grow: 1 !important;
    text-align: left !important;
    color: black !important;
    border-radius: 9999px !important;
    padding: 8px 80px 8px 16px !important; /* 0.5rem = 8px, 1rem = 16px */
    width: 220px !important;
    background-color: ${props =>
        props.overrideInputbg ? props.overrideInputbg : props.isDarkMode ? '#EDF0F1' : 'white'} !important;
    border: 1px solid #999999 !important;
    resize: none !important;
    overflow: hidden !important;
    line-height: 20px !important;
    min-height: 0px !important;
    height: ${props => {
        const text = props.value || '';
        const lines = text.toString().length <= 26 ? 1 : text.toString().length <= 52 ? 2 : 3;
        console.log(lines);
        return lines === 1 ? '20px' : lines === 2 ? '36px' : '56px';
    }} !important;
    &:focus {
        outline: none !important;
        border-radius: 8px !important; /* 0.5rem = 8px */
        border-color: ${props => props.isPurchase ? '#23C68D' : '#5942D1'} !important;
        box-shadow: 0 0 0 2px ${props => props.isPurchase ? '#23C68D' : '#5942D1'} !important;
        
        @media (min-width: 768px) {
        border-color: #5942D1 !important;
        box-shadow: 0 0 0 2px #5942D1 !important;
        }
    }
    &::placeholder {
        color: #888888 !important;
        font-size: 14px !important;
    }
  }
`;

const EmojiContainer = styled.div<{ hasMessage: boolean }>`
  position: absolute !important;
  width: 36px !important; /* 2.25rem = 36px */
  right: ${props => props.hasMessage ? '36px' : '32px'} !important; /* 2.25rem = 36px, 2rem = 32px */
`;

const SendButton = styled(BsArrowRightCircle) <{ isPurchase: boolean }>`
  position: absolute !important;
  right: 12px !important; /* 0.75rem = 12px */
  width: 24px !important; /* 1.5rem = 24px */
  height: 24px !important; /* 1.5rem = 24px */
  border-radius: 9999px !important;
  font-size: 30px !important; /* 1.875rem = 30px */
  color: white !important;
  font-weight: 800 !important;
  cursor: pointer !important;
  background-color: #5942D1 !important;
  
  @media (max-width: 768px) {
    background-color: #23C68D !important;
  }
`;

const ArrowIcon = styled.img`
  position: absolute !important;
  right: 12px !important; /* 0.75rem = 12px */
  cursor: pointer !important;
  font-size: 20px !important; /* 1.25rem = 20px */
  width: 16px !important; /* 1rem = 16px */
  height: 16px !important; /* 1rem = 16px */
`;

const CharacterCount = styled.p<{ isDarkMode: boolean; inverted: boolean }>`
  font-size: 12px !important;
  margin: 4px 0 0 0 !important; /* 0.25rem = 4px */
  display: flex !important;
  flex-direction: row !important;
  align-items: flex-end !important;
  align-self: flex-end !important;
  line-height: 20px !important;
  color: ${props =>
        props.isDarkMode
            ? (props.inverted ? 'black' : 'white')
            : (props.inverted ? 'white' : 'black')} !important;
`;

interface IReplyInputProps {
    commentId?: number;
    placeholder?: string;
    label?: string;
    containerClasses?: string;
    overrideInputbg?: string;
    inverted?: boolean;
    onFocus?: () => void;
    onSubmit: (message: string, values: Partial<User> | undefined) => void;
}

const CommentInput: React.FC<IReplyInputProps> = ({
    placeholder = "Type here",
    label,
    containerClasses = '',
    overrideInputbg,
    inverted = true,
    onFocus,
    onSubmit
}) => {
    const [message, setMessage] = useState<string>('');
    const { isLoggedIn } = useUserSession();
    const { state, dispatch } = useStateContext();
    const { isPurchase } = useAppContext();
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const maxLength = 200;

    const handleSubmit = () => {
        if (message.trim() === '') {
            // TODO show error
            return;
        }
        if (!isLoggedIn) {
            dispatch(openUserForm({
                commentText: message,
                label: label || 'Post my reply',
                onSuccess: (values) => onSubmit(message, values),
                onClose: () => { }
            }));
            return;
        }
        onSubmit(message, undefined);
        setMessage('');
    };

    const handleEmojiSelect = (emoji: EmojiMartData) => {
        if ('native' in emoji) {
            setMessage((currentMessage) => currentMessage + emoji.native);
            if (textareaRef.current) {
                textareaRef.current.focus();
            }
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(e.target.value);

        if (textareaRef.current) {
            // Adjust the textarea height to match the content
            textareaRef.current.style.height = 'auto';
            const numberOfLines = Math.floor(textareaRef.current.value.length / 28);
            textareaRef.current.style.height = `${Math.min(18 + (numberOfLines + 1) * 18, 3 * 20)}px`; // Max 3 lines height
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault(); // Prevent the default behavior of adding a new line
            handleSubmit(); // Call the submit function
        }
    };

    const handleInputFocus = () => {
        if (onFocus) onFocus();
    };

    const isDarkMode = state.themeMode === 'dark';

    return (
        <Container className="comment-container">
            <InputContainer width={containerClasses}>
                <TextareaWrapper>
                    <StyledTextarea
                        ref={textareaRef}
                        placeholder={placeholder}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        value={message}
                        maxLength={maxLength}
                        rows={1}
                        onFocus={handleInputFocus}
                        isDarkMode={isDarkMode}
                        isPurchase={!!isPurchase}
                        overrideInputbg={overrideInputbg}
                    />
                    <EmojiContainer hasMessage={message.length > 0}>
                        <EmojiInput onEmojiSelect={handleEmojiSelect} />
                    </EmojiContainer>
                    {message ? (
                        <SendButton
                            onClick={handleSubmit}
                            isPurchase={!!isPurchase}
                        />
                    ) : (
                        <ArrowIcon src={ArrowRightIcon} />
                    )}
                </TextareaWrapper>
                {message.length > 0 && (
                    <CharacterCount isDarkMode={isDarkMode} inverted={inverted}>
                        {maxLength - message.length} characters left
                    </CharacterCount>
                )}
            </InputContainer>
        </Container>
    );
};

export default CommentInput;
